import { Dispatch } from 'redux';
import _truncate from 'lodash/truncate';

import * as columnActions from '../../actions/column';
import * as columnItemActions from '../../actions/columnItem';
import * as canvasBlocksActions from '../../actions/canvasBlocks';
import { setSBBlockPopulating } from '../../actions/thirdPartyContent';
import store from '../../configureStore';
import {
    removeUnsupportedAttributes,
    removeUnsupportedElements,
    supportedElements,
} from '../../components/Canvas/CanvasBlock/Columns/ColumnItems/ColumnItem/Element/ParagraphElement/HelperMethods/PostPasteProcess';
import getImageAspectRatio from '../getImageAspectRatio';
import { TAppState } from '../../reducers';
import LANGUAGES from '../../constants/languages';
import elementTypes from '../../constants/elementTypes';

const { getState, dispatch, subscribe }: { getState: () => TAppState; dispatch: Dispatch; subscribe: any } = store;

interface Props {
    contents: Record<string, any>;
    blockIndex: string;
    columnIdx: string;
}

export const populateThirdPartyBlock = async ({ blockIndex, columnIdx, contents }: Props) => {
    dispatch(setSBBlockPopulating({ blockIndex }));
    let isFirstRun = true;
    dispatch(
        columnActions.splitColumn({
            blockIndex,
            columnIdx,
        })
    );
    dispatch(columnActions.updateColumnSizing({ blockID: blockIndex, newValue: [35] }));

    const unsubscribe = subscribe(async () => {
        const {
            canvasBlocks: { present: updatedState },
            session: {
                user: {
                    customization: { linkDecoration, linkColor },
                },
            },
        } = getState();
        const updatedBlock = updatedState.find((currentBlock) => currentBlock.index === blockIndex);

        // Redux store.subscribe() callback fires after each dispatch, and we only want the following code to run once
        if (updatedBlock && updatedBlock.content.columnNum === 2 && isFirstRun) {
            isFirstRun = false;
            const leftColumnIdx = updatedBlock.content.columns[0].columnIdx;
            const rightColumnIdx = updatedBlock.content.columns[1].columnIdx;
            dispatch(
                columnItemActions.addNewColumnItem({
                    blockIndex,
                    columnIdx: leftColumnIdx,
                    itemType: elementTypes.IMAGE,
                    initialContent: {
                        src: contents.image || 'PLACE_HOLDER',
                        aspectRatio: await getImageAspectRatio(contents.image || 'PLACE_HOLDER'),
                    },
                })
            );
            dispatch(
                columnItemActions.addNewColumnItem({
                    blockIndex,
                    columnIdx: rightColumnIdx,
                    itemType: elementTypes.PARAGRAPH,
                    initialContent: {
                        textMarkupValue: `<h3>${contents.title}</h3>`,
                        paddingTop: 6,
                        paddingBottom: 0,
                    },
                })
            );
            // Sanitize teaser contents
            const parsedTeaserHTML = new DOMParser().parseFromString(contents.teaser, 'text/html').body;
            const htmlCollection = parsedTeaserHTML.querySelectorAll('*');
            const htmlTagNames: string[] = [];
            htmlCollection.forEach((element) => {
                const tag = element.tagName.toLowerCase();
                if (!htmlTagNames.includes(tag)) {
                    htmlTagNames.push(tag);
                }
            });
            htmlTagNames.forEach((element) => {
                if (supportedElements.includes(element)) {
                    removeUnsupportedAttributes(parsedTeaserHTML, element);
                } else {
                    removeUnsupportedElements(parsedTeaserHTML, element);
                }
            });
            const teaserExists =
                parsedTeaserHTML.innerHTML &&
                parsedTeaserHTML.innerHTML !== 'undefined' &&
                parsedTeaserHTML.innerHTML !== '';
            dispatch(
                columnItemActions.addNewColumnItem({
                    blockIndex,
                    columnIdx: rightColumnIdx,
                    itemType: elementTypes.PARAGRAPH,
                    initialContent: {
                        textMarkupValue: `<p>${
                            teaserExists
                                ? _truncate(parsedTeaserHTML.innerHTML, {
                                      length: 250,
                                      separator: /\s+/,
                                  })
                                : 'This article’s teaser text is blank. Write an engaging, brief description here.'
                        }</p>`,
                        paddingTop: 10,
                        paddingBottom: 10,
                    },
                })
            );
            dispatch(
                columnItemActions.addNewColumnItem({
                    blockIndex,
                    columnIdx: rightColumnIdx,
                    itemType: elementTypes.PARAGRAPH,
                    initialContent: {
                        textMarkupValue: `<a href="${
                            contents.postUrl
                        }?utm_source=bananatag&utm_medium=email-notification"><strong><span style="color:${linkColor};text-decoration:${linkDecoration};">${
                            LANGUAGES[contents.language as keyof typeof LANGUAGES].readMore
                        } »</span></strong></a>`,
                        paddingTop: 0,
                    },
                })
            );
            dispatch(
                canvasBlocksActions.updateBlocksProperties({
                    isMetaData: true,
                    blockIDs: [blockIndex],
                    property: { keys: ['title'], value: 'Staffbase teaser populated' },
                })
            );
            unsubscribe();
            await new Promise<void>((res) => setTimeout(() => res(), 200));
            dispatch(setSBBlockPopulating({ blockIndex: '' }));
        }
    });
};
